import {
  styled,
  useDimension,
  useStyledTheme,
} from '@openeducation/oe-rn-themes';
import React from 'react';
import {
  DimensionValue,
  SafeAreaView,
  ScrollView,
  StyleSheet,
} from 'react-native';

import FeedbackDialogContent from './components/feedback-dialog-content';
import { FeedbackError } from './components/feedback-error';
import FeedbackSuccess from './components/feedback-success';
import { OEModal } from '../../atoms';
import { minHeight } from '../../style-utils';

export type FeedbackDialogUIProps = {
  visible: boolean;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  successNode?: React.ReactNode;
  nlsKeys: {
    title?: string;
    instructions?: string;
    enterText?: string;
    newQuestion?: string;
  };
  onClose: (message?: string) => void;
  onReset: (message?: string) => void;
  onSubmit: (message: string) => void;
};

export function FeedbackDialogUI({
  visible,
  isError,
  isLoading,
  isSuccess,
  successNode,
  nlsKeys,
  onClose,
  onReset,
  onSubmit,
}: FeedbackDialogUIProps) {
  const { color } = useStyledTheme();

  const { isLargeScreen } = useDimension();

  return (
    <OEModal
      visible={visible}
      style={[
        modalContentStyle(isLargeScreen),
        minHeight(380),
        { backgroundColor: color.grayLight },
      ]}
      showBackButton={!isLargeScreen}
      showCloseButton={isLargeScreen}
      onClose={onClose}
    >
      <StyledSafeAreaView>
        <ScrollView keyboardShouldPersistTaps="handled">
          {!isSuccess && !isError && (
            <FeedbackDialogContent
              isLoading={isLoading}
              nlsKeys={nlsKeys}
              onClose={onClose}
              onSubmit={onSubmit}
            />
          )}

          {isSuccess && (
            <FeedbackSuccess
              onReset={onReset}
              newQuestionKey={nlsKeys.newQuestion}
            >
              {successNode}
            </FeedbackSuccess>
          )}

          {isError && <FeedbackError onTryAgain={onReset} />}
        </ScrollView>
      </StyledSafeAreaView>
    </OEModal>
  );
}

const StyledSafeAreaView = styled(SafeAreaView)`
  height: 100%;
  justify-content: center;
`;

function modalContentStyle(isLargeScreen: boolean) {
  const screenSizeDependantStyle = isLargeScreen
    ? {
        maxHeight: 400 as DimensionValue,
        height: 'auto' as DimensionValue,
        borderRadius: 8,
      }
    : {
        maxHeight: '100%' as DimensionValue,
        height: '100%' as DimensionValue,
        borderRadius: 0,
      };

  const { modalContent } = StyleSheet.create({
    // eslint-disable-next-line react-native/no-unused-styles
    modalContent: {
      width: '100%',
      maxWidth: 800,
      alignSelf: 'center',
      overflow: 'hidden',
      ...screenSizeDependantStyle,
    },
  });

  return modalContent;
}
